import { createContext, useContext, useState, useCallback, useEffect } from "react";

const PrivacyContext = createContext(null);
export const MASK = "••••••";

export function PrivacyProvider({ children }) {
    const [hidden, setHidden] = useState(() => localStorage.getItem("apex_hide_values") === "1");
    const [autoLock, setAutoLockState] = useState(() => localStorage.getItem("apex_autolock") === "1");
    const [autoLockMin, setAutoLockMinState] = useState(() => Number(localStorage.getItem("apex_autolock_min")) || 5);
    const [lockOnBlur, setLockOnBlurState] = useState(() => localStorage.getItem("apex_lockonblur") === "1");

    const setHiddenPersist = useCallback((v) => {
        localStorage.setItem("apex_hide_values", v ? "1" : "0");
        setHidden(v);
    }, []);

    const toggle = useCallback(() => setHiddenPersist(!hidden), [hidden, setHiddenPersist]);
    const mask = useCallback((formatted) => (hidden ? MASK : formatted), [hidden]);

    const setAutoLock = useCallback((v) => {
        localStorage.setItem("apex_autolock", v ? "1" : "0");
        setAutoLockState(v);
    }, []);
    const setAutoLockMin = useCallback((v) => {
        localStorage.setItem("apex_autolock_min", String(v));
        setAutoLockMinState(v);
    }, []);
    const setLockOnBlur = useCallback((v) => {
        localStorage.setItem("apex_lockonblur", v ? "1" : "0");
        setLockOnBlurState(v);
    }, []);

    // lock on tab switch / minimize / window blur
    useEffect(() => {
        if (!lockOnBlur) return;
        const onHide = () => { if (document.hidden) setHiddenPersist(true); };
        const onBlur = () => setHiddenPersist(true);
        document.addEventListener("visibilitychange", onHide);
        window.addEventListener("blur", onBlur);
        return () => {
            document.removeEventListener("visibilitychange", onHide);
            window.removeEventListener("blur", onBlur);
        };
    }, [lockOnBlur, setHiddenPersist]);

    // idle auto-lock
    useEffect(() => {
        if (!autoLock) return;
        let timer;
        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(() => setHiddenPersist(true), Math.max(1, autoLockMin) * 60 * 1000);
        };
        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearTimeout(timer);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [autoLock, autoLockMin, setHiddenPersist]);

    return (
        <PrivacyContext.Provider value={{ hidden, toggle, mask, autoLock, setAutoLock, autoLockMin, setAutoLockMin, lockOnBlur, setLockOnBlur }}>
            {children}
        </PrivacyContext.Provider>
    );
}

export const usePrivacy = () => useContext(PrivacyContext);
